import { create } from 'zustand';
import { ApiError } from '../lib/api';

export type ToastVariant = 'error' | 'info';

export interface Toast {
  id: number;
  variant: ToastVariant;
  /** One-line headline (Geist 13/500). */
  title: string;
  /** Optional supporting sentence (Geist 13/400, muted). */
  description?: string;
}

interface ToastState {
  toasts: Toast[];
  /** Enqueue a toast; returns its id so callers can dismiss it early. */
  push: (toast: Omit<Toast, 'id'>) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Surface a thrown backend error as an error toast. Pulls the friendly
 *  message out of an ApiError (and explains the structured details we know,
 *  like the template-publish `refs` list); falls back to a generic title for
 *  anything else. Safe to call from mutation `onError` handlers — it reads the
 *  store imperatively, no hook required. */
export function toastApiError(err: unknown, fallbackTitle = 'Something went wrong'): void {
  const { push } = useToastStore.getState();

  if (err instanceof ApiError && typeof err.body === 'object') {
    const refs = err.body.refs;
    if (Array.isArray(refs) && refs.length > 0) {
      const n = refs.length;
      push({
        variant: 'error',
        title: err.message,
        description: `${n} asset${n === 1 ? '' : 's'} must come from your library. Replace ${
          n === 1 ? 'it' : 'them'
        }, then publish again.`,
      });
      return;
    }
  }

  push({
    variant: 'error',
    title: err instanceof ApiError ? err.message : fallbackTitle,
    description: err instanceof ApiError ? undefined : err instanceof Error ? err.message : undefined,
  });
}
